"use client";
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../../components/ui/tabs";
import { Button } from "../../../components/ui/button";
import { Order } from "../types/order";
import { OrderCard } from "./OrderCard";
import { OrderForm } from "./OrderForm";
import { getOrderDeliveryText } from "./orderDeliveryText";
import {
  resolveQuotationGstKind,
  buildQuotationGstSummaryRows,
  appendFreightToGstRateMap,
  aggregateQuotationGstSummaryAmounts,
  quotationGstSupplyTypeLabel,
  formatQuotationMoney,
  parseQuotationMoney,
  getOrderFixingDiscount,
  buildCsvNoteColumns,
  quotationCsvCell,
  getCsvProductName,
  normalizeOrderProducts,
  QUOTATION_CSV_ORDER_DETAIL_BLANK_COUNT,
  QUOTATION_CSV_NOTE_COLUMN_COUNT,
  assertQuotationCsvRowWidth,
} from "./quotationPdfShared";
import {
  loadQuotationListState,
  saveQuotationListState,
} from "./quotationListState";
import axios from "axios";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { API_BASE_URL } from "../../../../src/App.js";
import { Plus, ChevronLeft, ChevronRight, ShoppingCart, Filter, X, Download, LayoutGrid, Table2, Pencil, ExternalLink } from "lucide-react";

const emptyFilters = {
  createdBy: "",
  customer: "",
  brandId: "",
  quotationSeries: "",
  fromDate: "",
  toDate: "",
};

export const OrdersPage: React.FC<{ userRole: "admin" | "dealer" | "employee"; userId: string }> = ({
  userRole,
  userId,
}) => {
  const savedList = useMemo(() => loadQuotationListState(), []);
  const [searchParams] = useSearchParams();
  const initialPageFromUrl = Number(searchParams.get("page"));
  const initialPage =
    (Number.isFinite(initialPageFromUrl) && initialPageFromUrl > 0
      ? initialPageFromUrl
      : savedList.page) || 1;

  const [orders, setOrders] = useState<Order[]>([]);
  const [tab, setTab] = useState(savedList.tab || "all");
  const [statuses, setStatuses] = useState<Array<{ id: number; label: string; color: string; sortOrder: number }>>([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  /** true = card grid; false = table (similar to Users desktop table). */
  const [cardView, setCardView] = useState(() => {
    try {
      return typeof window !== "undefined" && localStorage.getItem("quotationListCardView") !== "false";
    } catch {
      return true;
    }
  });
  const [page, setPage] = useState(initialPage);
  const [totalCount, setTotalCount] = useState(0);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
const [fromDate, setFromDate] = useState<string>(savedList.appliedFilters?.fromDate || "");
const [toDate, setToDate] = useState<string>(savedList.appliedFilters?.toDate || "");

const [users, setUsers] = useState<any[]>([]);
const [customers, setCustomers] = useState<any[]>([]);
const [brands, setBrands] = useState<any[]>([]);
const [quotationSeriesOptions, setQuotationSeriesOptions] = useState<string[]>([]);
const [productBrandMap, setProductBrandMap] = useState<Record<string, number>>({});
const [showFilters, setShowFilters] = useState(false);
const [loading, setLoading] = useState(false);
const [csvDownloading, setCsvDownloading] = useState(false);
const [appliedFilters, setAppliedFilters] = useState({
  ...emptyFilters,
  ...(savedList.appliedFilters || {}),
});
const skipFilterPageReset = useRef(true);
const { authToken = "", id = "" ,role="" } = useSelector(
    (state: any) => state.loginData || {}
  );
  const navigate = useNavigate();
  /** Tailwind `md` (768px): table vs cards toggle exists only at this breakpoint and up; below = always cards. */
  const [isMdUp, setIsMdUp] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(min-width: 768px)").matches : true
  );

  const [maxResults, setMaxResults] = useState(savedList.maxResults || 10);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(min-width: 768px)");
    const onChange = () => setIsMdUp(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const persistCardView = useCallback((next: boolean) => {
    setCardView(next);
    try {
      localStorage.setItem("quotationListCardView", next ? "true" : "false");
    } catch {
      /* ignore */
    }
  }, []);

  const showCardLayout = !isMdUp || cardView;

  const fetchStatuses = useCallback(async () => {
    if (!id || !authToken?.trim()) return;
    try {
      const res = await axios.get(`${API_BASE_URL}account/${id}/order-statuses`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setStatuses(res.data.data || []);
    } catch (err) {
      console.error("Error fetching order statuses", err);
    }
  }, [id, authToken]);

  const fetchStatusCounts = useCallback(async (currentStatuses: typeof statuses, filters = appliedFilters) => {
    if (!id || !authToken?.trim()) return;
    const allLabels = ["all", ...currentStatuses.map((s) => s.label)];
    try {
      const results = await Promise.all(
        allLabels.map((label) =>
          axios
            .get(`${API_BASE_URL}account/${id}/orders`, {
              params: {
                firstResults: 0,
                maxResults: 1,
                status: label,
                createdBy: filters.createdBy,
                customer: filters.customer,
                brandId: filters.brandId,
                quotationSeries: filters.quotationSeries,
                fromDate: filters.fromDate,
                toDate: filters.toDate,
              },
              headers: { Authorization: `Bearer ${authToken}` },
            })
            .then((res) => ({ label, count: res.data.totalCount || 0 }))
            .catch(() => ({ label, count: 0 }))
        )
      );
      const counts: Record<string, number> = {};
      results.forEach(({ label, count }) => { counts[label] = count; });
      setStatusCounts(counts);
    } catch (err) {
      console.error("Error fetching status counts", err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, authToken, appliedFilters]);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}account/${id}/users`, {
        params: { firstResults: 0, maxResults: "all" },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setUsers(res.data.users || []);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  }, [id, authToken]);

  const fetchCustomers = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}account/${id}/customers`, {
        params: { firstResults: 0, maxResults: 999, search: "" },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setCustomers(res.data.customers || []);
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  }, [id, authToken]);

  const fetchFilterOptions = useCallback(async () => {
    if (!id || !authToken?.trim()) return;
    const headers = { Authorization: `Bearer ${authToken}` };
    const [brandsResult, productsResult, seriesResult, masterSeriesResult] = await Promise.allSettled([
      axios.get(`${API_BASE_URL}account/${id}/brands`, {
        params: { firstResults: 0, maxResults: 1000 },
        headers,
      }),
      axios.get(`${API_BASE_URL}account/${id}/products`, {
        params: { firstResults: 0, maxResults: 10000 },
        headers,
      }),
      axios.get(`${API_BASE_URL}account/${id}/orders/quotation-series`, { headers }),
      axios.get(`${API_BASE_URL}account/${id}/quotation-series`, {
        params: { firstResults: 0, maxResults: 1000 },
        headers,
      }),
    ]);
    if (brandsResult.status === "fulfilled") {
      setBrands(brandsResult.value.data.brands || []);
    }
    if (productsResult.status === "fulfilled") {
      const map: Record<string, number> = {};
      (productsResult.value.data.products || []).forEach((product: any) => {
        map[String(product.id)] = Number(product.brandId || 0);
      });
      setProductBrandMap(map);
    }
    const orderSeries = seriesResult.status === "fulfilled"
      ? seriesResult.value.data.quotationSeries || seriesResult.value.data.series || seriesResult.value.data.data || []
      : [];
    const masterSeries = masterSeriesResult.status === "fulfilled"
      ? (masterSeriesResult.value.data.series || []).map((item: any) => String(item.name || "").trim()).filter(Boolean)
      : [];
    setQuotationSeriesOptions(
      Array.from(
        new Set([
          ...(Array.isArray(masterSeries) ? masterSeries : []),
          ...(Array.isArray(orderSeries) ? orderSeries : []),
        ].filter(Boolean)),
      ).sort(),
    );
  }, [authToken, id]);


useEffect(() => {
  fetchUsers();
  fetchCustomers();
  fetchStatuses();
  fetchFilterOptions();
}, [fetchStatuses, fetchUsers, fetchCustomers, fetchFilterOptions]);

useEffect(() => {
  if (statuses.length > 0) fetchStatusCounts(statuses);
}, [statuses, fetchStatusCounts]);


  // 🧠 Fetch orders from backend
const fetchOrders = useCallback(async (pageNum = 1) => {
  setLoading(true);
  try {
    const firstResults = (pageNum - 1) * maxResults;
    const res = await axios.get(`${API_BASE_URL}account/${id}/orders`, {
      params: {
        firstResults,
        maxResults,
        status: tab,
        createdBy: appliedFilters.createdBy,
        customer: appliedFilters.customer,
        brandId: appliedFilters.brandId,
        quotationSeries: appliedFilters.quotationSeries,
        fromDate: appliedFilters.fromDate,
        toDate: appliedFilters.toDate,
      },
      headers: { Authorization: `Bearer ${authToken}` },
    });
    setOrders(res.data.orders || []);
    setTotalCount(res.data.totalCount || 0);
  } catch (error) {
    console.error("Error fetching orders:", error);
  } finally {
    setLoading(false);
  }
}, [id, authToken, tab, appliedFilters, maxResults]);

  // Reset to page 1 when filters/tab/page-size change (not on first mount — restore saved page).
  useEffect(() => {
    if (skipFilterPageReset.current) {
      skipFilterPageReset.current = false;
      return;
    }
    setPage(1);
  }, [tab, appliedFilters, maxResults]);

  // Fetch current page and persist list state for detail → back navigation.
  useEffect(() => {
    fetchOrders(page);
    saveQuotationListState({ page, maxResults, tab, appliedFilters });
  }, [page, fetchOrders, maxResults, tab, appliedFilters]);

  const openOrderDetail = useCallback(
    (orderId: string | number) => {
      saveQuotationListState({ page, maxResults, tab, appliedFilters });
      navigate(`/panel/orders/${orderId}`);
    },
    [navigate, page, maxResults, tab, appliedFilters],
  );

  

  // 🧭 Pagination
  const totalPages = useMemo(() => Math.ceil(totalCount / maxResults) || 0, [totalCount, maxResults]);

  // If filters shrink the result set, stay on a valid page.
  useEffect(() => {
    if (totalPages > 0 && page > totalPages) {
      setPage(totalPages);
    }
  }, [totalPages, page]);

  const pageNumbers = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, index) => index + 1);
    const pages = new Set([1, totalPages, page - 1, page, page + 1]);
    return Array.from(pages)
      .filter((value) => value >= 1 && value <= totalPages)
      .sort((a, b) => a - b);
  }, [page, totalPages]);

  const handlePrev = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleNext = () => {
    if (page < totalPages) {
      setPage(page + 1);
    }
  };

  const handleDownloadCSV = async () => {
    if (csvDownloading) return;
    setCsvDownloading(true);
    try {
      // Match list filters. Do not force createdBy=username — that emptied admin CSV in production
      // (admin list shows all; staff are already scoped by the API via their role).
      const [ordersRes, settingsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}account/${id}/orders`, {
          params: {
            firstResults: 0,
            maxResults: 9999,
            status: tab,
            createdBy: appliedFilters.createdBy,
            customer: appliedFilters.customer,
            brandId: appliedFilters.brandId,
            quotationSeries: appliedFilters.quotationSeries,
            fromDate: appliedFilters.fromDate,
            toDate: appliedFilters.toDate,
          },
          headers: { Authorization: `Bearer ${authToken}` },
          timeout: 120000,
        }),
        axios.get(`${API_BASE_URL}account/${id}/settings`, {
          headers: { Authorization: `Bearer ${authToken}` },
          timeout: 30000,
        }),
      ]);

      const companyCountry = String(settingsRes.data?.companyCountry ?? "India").trim() || "India";
      const companyState = String(settingsRes.data?.companyState ?? "").trim();

      const allOrders: Order[] = ordersRes.data.orders || [];
      const sortedOrders = allOrders.sort((a: any, b: any) => (parseInt(a.id) || 0) - (parseInt(b.id) || 0));

      // Fetch notes in small batches (full parallel can fail / hang in production).
      const notesMap: Record<string, any[]> = {};
      const noteBatchSize = 8;
      for (let i = 0; i < sortedOrders.length; i += noteBatchSize) {
        const batch = sortedOrders.slice(i, i + noteBatchSize);
        await Promise.all(
          batch.map(async (o: any) => {
            try {
              const nr = await axios.get(`${API_BASE_URL}account/${id}/order/${o.id}/notes`, {
                params: { firstResults: 0, maxResults: 100 },
                headers: { Authorization: `Bearer ${authToken}` },
                timeout: 20000,
              });
              const raw = nr.data?.notes ?? nr.data;
              notesMap[o.id] = Array.isArray(raw) ? raw : [];
            } catch {
              notesMap[o.id] = [];
            }
          }),
        );
      }

      const quotationExtraCols = [
        "Customer Country",
        "Customer State",
        "Customer GSTIN",
        "Company Country",
        "Company State",
        "GST supply type",
        "CGST (total)",
        "SGST (total)",
        "IGST (total)",
        "Overseas GST (total)",
        "Taxable subtotal",
        "Total tax",
        "Discount",
        "Freight",
        "Grand total (rounded)",
      ];

      const headers = [
        "Order ID","Quotation Number", "Status", "Customer Name", "Customer ID",
        "Product Name", "Brand", "Quantity", "Unit Price", "Price", "GST", "Total",
        "Subject", "Kind Attention", "Validity", "Freight Charges", "Delivery", "Created By", "Created At",
        ...quotationExtraCols,
        "Note 1", "Note 1 By", "Note 1 Date & Time",
        "Note 2", "Note 2 By", "Note 2 Date & Time",
        "Note 3", "Note 3 By", "Note 3 Date & Time",
      ];

      const emptyQuotationExtras = quotationExtraCols.map(() => "");

      const gstExtrasForOrder = (o: any) => {
        const allProducts = normalizeOrderProducts(o);
        const gstRateMap: Record<number, number> = {};
        allProducts.forEach((p: any) => {
          const rate = parseFloat(p.gst?.toString() || "0");
          const base = parseFloat(p.price?.toString() || "0") * parseFloat(p.nos?.toString() || "1");
          if (rate > 0) {
            gstRateMap[rate] = (gstRateMap[rate] || 0) + base;
          }
        });
        const kind = resolveQuotationGstKind(
          String(o.country_ ?? "India"),
          companyCountry,
          String(o.state_ ?? ""),
          companyState,
        );
        const freightBase = parseFloat(o.freightCharges || o.transport) || 0;
        const freightPct =
          parseFloat(String(o.freightChargesGst ?? o.freight_charges_gst_ ?? "").replace(/,/g, "")) || 18;
        const gstSummaryRows = buildQuotationGstSummaryRows(
          appendFreightToGstRateMap(gstRateMap, freightBase, freightPct),
          kind,
        );
        const productAgg = aggregateQuotationGstSummaryAmounts(gstSummaryRows);
        const subTotal = parseQuotationMoney(o.price);
        const { fixing, discount } = getOrderFixingDiscount(o);
        // productAgg.total already includes freight GST folded into CGST/SGST/IGST.
        const grandTotalRounded = Math.round(
          subTotal + freightBase + fixing + productAgg.total - discount,
        );
        const custGst = o.gst_ || o.customerGst_ || "";
        return [
          o.country_ || "India",
          o.state_ || "",
          custGst,
          companyCountry,
          companyState,
          quotationGstSupplyTypeLabel(kind),
          productAgg.cgst.toFixed(2),
          productAgg.sgst.toFixed(2),
          productAgg.igst.toFixed(2),
          productAgg.overseasGst.toFixed(2),
          subTotal.toFixed(2),
          productAgg.total.toFixed(2),
          discount.toFixed(2),
          (freightBase).toFixed(2),
          grandTotalRounded.toFixed(2),
        ];
      };

      const headerCount = headers.length;
      const orderDetailBlanks = () => Array(QUOTATION_CSV_ORDER_DETAIL_BLANK_COUNT).fill("");
      const noteBlanks = () => Array(QUOTATION_CSV_NOTE_COLUMN_COUNT).fill("");
      const extraBlanks = () => [...emptyQuotationExtras];

      const productCsvCells = (product: Record<string, unknown>) => {
        const qty = parseQuotationMoney(product.nos ?? 1) || 1;
        const unitPrice = parseQuotationMoney(product.price);
        const productId = String(product.product_id ?? product.id ?? "");
        const brandId = Number(product.brandId ?? product.brand_id ?? productBrandMap[productId] ?? 0);
        const brandName =
          String(product.brandName ?? product.brand_name ?? "").trim() ||
          String(brands.find((brand) => Number(brand.id) === brandId)?.name || "");
        return [getCsvProductName(product), brandName, qty, formatQuotationMoney(unitPrice)];
      };

      const orderLeadCells = (o: any) => [
        o.id,
        o.quotationNumber || "",
        o.status,
        o.customerName || "",
        o.customerId || "",
      ];

      const rows: any[] = [];
      sortedOrders.forEach((o: any) => {
        const orderNotes = notesMap[o.id] || [];
        const noteCols = buildCsvNoteColumns(orderNotes, o.notes);
        const qx = gstExtrasForOrder(o);
        const productsList = normalizeOrderProducts(o);
        if (productsList.length > 0) {
          productsList.forEach((product, index) => {
            const isFirst = index === 0;
            const row = [
              ...orderLeadCells(o),
              ...productCsvCells(product),
              ...(isFirst
                ? [
                    formatQuotationMoney(o.price),
                    formatQuotationMoney(o.gst),
                    formatQuotationMoney(o.total),
                    o.subject || "",
                    o.kindAttention ||
                      `${o.person_name_ || ""}${o.person_name_ && o.person_mobile_ ? " / " : ""}${o.person_mobile_ || ""}`,
                    o.validity || "",
                    o.freightCharges || "",
                    getOrderDeliveryText(o),
                    o.createdBy,
                    new Date(o.createdAt).toLocaleDateString(),
                    ...qx,
                    ...noteCols,
                  ]
                : [...orderDetailBlanks(), ...extraBlanks(), ...noteBlanks()]),
            ];
            assertQuotationCsvRowWidth(row, headerCount, `order ${o.id} product ${index + 1}`);
            rows.push(row);
          });
        } else {
          const row = [
            o.id,
            o.quotationNumber || "",
            o.status,
            o.customerName || "",
            o.customerId || "",
            "",
            "",
            "",
            "",
            formatQuotationMoney(o.price),
            formatQuotationMoney(o.gst),
            formatQuotationMoney(o.total),
            o.subject || "",
            o.kindAttention ||
              `${o.person_name_ || ""}${o.person_name_ && o.person_mobile_ ? " / " : ""}${o.person_mobile_ || ""}`,
            o.validity || "",
            o.freightCharges || "",
            getOrderDeliveryText(o),
            o.createdBy,
            new Date(o.createdAt).toLocaleDateString(),
            ...qx,
            ...noteCols,
          ];
          assertQuotationCsvRowWidth(row, headerCount, `order ${o.id} no products`);
          rows.push(row);
        }
      });

      const csv = [headers, ...rows]
        .map((r: any[]) => r.map((v: any) => quotationCsvCell(v)).join(","))
        .join("\n");
      const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `quotations_${Date.now()}.csv`;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`CSV downloaded (${sortedOrders.length} quotation${sortedOrders.length === 1 ? "" : "s"})`);
    } catch (err) {
      console.error("Failed to download CSV", err);
      toast.error("Failed to download CSV. Please try again.");
    } finally {
      setCsvDownloading(false);
    }
  };

  return (
    <div className="space-y-6 bg-white">
      {/* Modern Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 flex items-center gap-3">
            <ShoppingCart className="text-[var(--sidebar-primary)]" size={36} />
            Quotations
          </h1>
          <p className="text-gray-500 text-sm mt-2">Manage and track all your quotations</p>
        </div>
        <div className="flex flex-col gap-2 w-full md:w-auto">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <div
              className="hidden md:inline-flex rounded-xl border-2 border-gray-200 p-1 bg-gray-50 self-start sm:self-auto"
              role="group"
              aria-label="Quotation list layout (desktop only)"
            >
              <button
                type="button"
                onClick={() => persistCardView(false)}
                className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition-all ${
                  !cardView
                    ? "bg-white text-[var(--sidebar-primary)] shadow-sm border border-gray-100"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                <Table2 size={18} />
                Table
              </button>
              <button
                type="button"
                onClick={() => persistCardView(true)}
                className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition-all ${
                  cardView
                    ? "bg-white text-[var(--sidebar-primary)] shadow-sm border border-gray-100"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                <LayoutGrid size={18} />
                Cards
              </button>
            </div>
            <Button
              onClick={() => {
                setSelectedOrder(null);
                setShowForm(true);
              }}
              className="btn-primary flex items-center gap-2 py-3 px-6 rounded-xl shadow-lg w-full sm:w-auto"
            >
              <Plus size={20} />
              Add Quotation
            </Button>
          </div>
          <p className="text-xs text-gray-500 md:hidden">Cards on mobile; table/cards toggle appears on desktop (768px+).</p>
        </div>
      </div>

      {/* Filters Section */}
      <div>
        {/* Mobile Filter Button */}
        <div className="lg:hidden mb-4">
          <Button
            onClick={() => setShowFilters(!showFilters)}
            className="w-full btn-primary flex items-center justify-center gap-2 py-3 px-6 rounded-xl shadow-lg"
          >
            {showFilters ? (
              <>
                <X size={20} />
                Hide Filters
              </>
            ) : (
              <>
                <Filter size={20} />
                Show Filters
              </>
            )}
          </Button>
        </div>

        {/* Filters Container */}
        <div className={`bg-white border-2 border-gray-200 rounded-xl p-3 overflow-x-auto ${
          showFilters ? 'block' : 'hidden lg:block'
        }`}>
          <div className="flex flex-row gap-2 items-end min-w-max">

            {/* Created By */}
            {role === "admin" && (
              <div className="flex flex-col w-32">
                <label className="text-xs font-medium text-gray-600 mb-1">Created By</label>
                <select
                  value={appliedFilters.createdBy}
                  onChange={(e) => {
                    setAppliedFilters(prev => ({ ...prev, createdBy: e.target.value }));
                    setPage(1);
                  }}
                  className="border rounded-lg px-2 py-2 text-sm"
                >
                  <option value="">All</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.username}>
                      {user.companyName || user.username}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Customer */}
            <div className="flex flex-col w-36">
              <label className="text-xs font-medium text-gray-600 mb-1">Customer</label>
              <select
                value={appliedFilters.customer}
                onChange={(e) => {
                  setAppliedFilters(prev => ({ ...prev, customer: e.target.value }));
                  setPage(1);
                }}
                className="border rounded-lg px-2 py-2 text-sm"
              >
                <option value="">All</option>
                {customers.map((c: any) => (
                  <option key={c.id} value={c.id}>{c.companyName}</option>
                ))}
              </select>
            </div>

            {/* Status */}
            <div className="flex flex-col w-36">
              <label className="text-xs font-medium text-gray-600 mb-1">Brand</label>
              <select
                value={appliedFilters.brandId}
                onChange={(e) => {
                  setAppliedFilters((prev) => ({ ...prev, brandId: e.target.value }));
                  setPage(1);
                }}
                className="border rounded-lg px-2 py-2 text-sm"
              >
                <option value="">All brands</option>
                {brands.map((brand: any) => (
                  <option key={brand.id} value={brand.id}>{brand.name}</option>
                ))}
              </select>
            </div>

            <div className="flex flex-col w-36">
              <label className="text-xs font-medium text-gray-600 mb-1">Quotation Series</label>
              <select
                value={appliedFilters.quotationSeries}
                onChange={(e) => {
                  setAppliedFilters((prev) => ({ ...prev, quotationSeries: e.target.value }));
                  setPage(1);
                }}
                className="border rounded-lg px-2 py-2 text-sm"
              >
                <option value="">All series</option>
                {quotationSeriesOptions.map((series) => (
                  <option key={series} value={series}>{series}</option>
                ))}
              </select>
            </div>

            {/* Status */}
            <div className="flex flex-col w-32">
              <label className="text-xs font-medium text-gray-600 mb-1">Status</label>
              <select
                value={tab}
                onChange={(e) => setTab(e.target.value)}
                className="border rounded-lg px-2 py-2 text-sm"
              >
                <option value="all">All</option>
                {statuses.map((s) => (
                  <option key={s.id} value={s.label}>{s.label}</option>
                ))}
              </select>
            </div>

            {/* From Date */}
            <div className="flex flex-col w-36">
              <label className="text-xs font-medium text-gray-600 mb-1">From</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="border rounded-lg px-2 py-2 text-sm"
              />
            </div>

            {/* To Date */}
            <div className="flex flex-col w-36">
              <label className="text-xs font-medium text-gray-600 mb-1">To</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="border rounded-lg px-2 py-2 text-sm"
              />
            </div>

            {/* Apply + Download CSV Buttons */}
            <div className="flex flex-row gap-2 items-end">
              <Button
                onClick={() => {
                  setAppliedFilters(prev => ({ ...prev, fromDate, toDate }));
                  setPage(1);
                }}
                className="btn-primary flex items-center gap-2 whitespace-nowrap"
              >
                Apply
              </Button>
              {role !== "dealer" && (
                <Button
                  onClick={handleDownloadCSV}
                  variant="outline"
                  disabled={csvDownloading}
                  className="btn-ghost flex items-center gap-2 whitespace-nowrap"
                  title="Download quotations matching the current filters"
                >
                  <Download size={16} />
                  {csvDownloading ? "Preparing CSV…" : "Download CSV"}
                </Button>
              )}
            </div>

          </div>
        </div>
      </div>

      {/* Status Tabs */}
      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <div className="flex flex-row flex-wrap gap-2 pb-2">
          {[{ id: 0, label: "All", color: "#3b82f6", sortOrder: 0 }, ...statuses].map((s) => {
            const tabKey = s.id === 0 ? "all" : s.label;
            const isActive = tab === tabKey;
            const count = statusCounts[tabKey];
            return (
              <button
                key={s.id}
                onClick={() => setTab(tabKey)}
                style={isActive ? { backgroundColor: s.color } : undefined}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm whitespace-nowrap transition-all ${
                  isActive
                    ? "text-white shadow-lg"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {s.label}
                {count !== undefined && (
                  <span
                    className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${
                      isActive ? "bg-white/30 text-white" : "bg-white text-gray-600 border border-gray-300"
                    }`}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Orders Grid */}
        <TabsContent value={tab} className="space-y-4 mt-4">
          {loading ? (
            <div className="flex justify-center items-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-[var(--sidebar-primary)] border-t-transparent"></div>
            </div>
          ) : orders.length === 0 ? (
            <div className="bg-white rounded-xl border-2 border-gray-200 p-12 text-center">
              <ShoppingCart size={48} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500 font-medium text-lg">No orders found</p>
              <p className="text-gray-400 text-sm mt-1">Create a new order to get started</p>
            </div>
          ) : showCardLayout ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {orders.map((order: Order) => (
                <OrderCard
                  key={order.id}
                  order={order}
                  refresh={() => fetchOrders(page)}
                  orderStatuses={statuses}
                  onEdit={(o) => {
                    setSelectedOrder(o);
                    setShowForm(true);
                  }}
                />
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px]">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gradient-to-r from-[var(--sidebar-light)] to-[var(--accent-primary)]/15">
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wide">Quotation</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wide">Customer</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wide">Status</th>
                      <th className="px-4 py-3 text-right text-xs font-bold text-gray-900 uppercase tracking-wide">Total</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wide">Created</th>
                      <th className="px-4 py-3 text-right text-xs font-bold text-gray-900 uppercase tracking-wide">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order: Order) => {
                      const matched = statuses.find(
                        (s) => s.label?.toLowerCase() === order.status?.toLowerCase()
                      );
                      const statusColor = matched?.color ?? "#6366f1";
                      const statusLabel = matched?.label ?? order.status ?? "—";
                      return (
                        <tr
                          key={order.id}
                          onClick={() => openOrderDetail(order.id)}
                          className="border-b border-gray-100 hover:bg-[var(--sidebar-light)]/40 cursor-pointer transition-colors group"
                        >
                          <td className="px-4 py-3 text-sm font-semibold text-gray-900 group-hover:text-[var(--sidebar-primary)]">
                            {order.quotationNumber || `#${order.id}`}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 max-w-[200px] truncate" title={order.customerName}>
                            {order.customerName || "—"}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              style={{
                                backgroundColor: `${statusColor}22`,
                                color: statusColor,
                                border: `1px solid ${statusColor}44`,
                              }}
                              className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-bold"
                            >
                              {statusLabel}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm font-semibold text-right text-gray-900">
                            ₹{parseFloat(String(order.total || "0")).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">
                            {order.createdAt ? new Date(order.createdAt).toLocaleDateString("en-IN") : "—"}
                          </td>
                          <td className="px-4 py-3 text-right space-x-2" onClick={(e) => e.stopPropagation()}>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="text-[var(--sidebar-primary)] border-[var(--sidebar-primary)]/30 hover:bg-[var(--sidebar-light)]"
                              onClick={() => openOrderDetail(order.id)}
                            >
                              <ExternalLink className="w-3.5 h-3.5 mr-1" />
                              Open
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="text-blue-700 border-blue-200 hover:bg-blue-50"
                              onClick={() => {
                                setSelectedOrder(order);
                                setShowForm(true);
                              }}
                            >
                              <Pencil className="w-3.5 h-3.5 mr-1" />
                              Edit
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>


      {/* Modern Pagination */}
      {totalCount > 0 && (
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 bg-white rounded-xl p-4 border-2 border-gray-200">
          <div className="flex flex-wrap items-center justify-center gap-3 sm:justify-start">
            <p className="text-sm font-medium text-gray-600">
              Showing{" "}
              <span className="font-bold text-[var(--sidebar-primary)]">
                {(page - 1) * maxResults + 1}–{Math.min(page * maxResults, totalCount)}
              </span>{" "}
              of <span className="font-bold text-[var(--sidebar-primary)]">{totalCount}</span>
            </p>
            <label className="flex items-center gap-2 text-sm text-gray-600">
              Per page
              <select
                value={maxResults}
                onChange={(event) => {
                  setMaxResults(Number(event.target.value));
                  setPage(1);
                }}
                className="rounded-lg border border-gray-300 bg-white px-2 py-1.5 font-semibold text-gray-800"
              >
                {[10, 25, 50, 100].map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </label>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <Button
              variant="outline"
              disabled={page === 1}
              onClick={handlePrev}
              className="flex items-center gap-2 disabled:opacity-50"
            >
              <ChevronLeft size={18} />
              <span className="hidden sm:inline">Previous</span>
            </Button>

            {pageNumbers.map((pageNumber, index) => (
              <React.Fragment key={pageNumber}>
                {index > 0 && pageNumber - pageNumbers[index - 1] > 1 && (
                  <span className="px-1 text-gray-400">…</span>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setPage(pageNumber);
                  }}
                  aria-current={pageNumber === page ? "page" : undefined}
                  className={`h-9 min-w-9 rounded-lg px-2 text-sm font-bold transition ${
                    pageNumber === page
                      ? "bg-[var(--sidebar-primary)] text-white shadow-sm"
                      : "border border-gray-200 bg-white text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  {pageNumber}
                </button>
              </React.Fragment>
            ))}

            <Button
              variant="outline"
              disabled={page === totalPages}
              onClick={handleNext}
              className="flex items-center gap-2 disabled:opacity-50"
            >
              <span className="hidden sm:inline">Next</span>
              <ChevronRight size={18} />
            </Button>
          </div>
        </div>
      )}

      {/* Order Form */}
      <OrderForm
        open={showForm}
        onClose={() => setShowForm(false)}
        refresh={() => fetchOrders(page)}
        userRole={userRole}
        userId={userId}
        existingOrder={selectedOrder}
      />
    </div>
  );
};
